import { Margin, Padding } from 'types';

import addHeaderClassesToHtml from './addHeaderClassesToHtml';
import { getCKEditorToken } from '../api/getCKEditorToken';
import { addBackgroundImage } from './addBackgroundImage';
import { addWatermark } from './addWatermark';
import { addContentPadding } from './addContentPadding';

const PDF_MARGIN_OPTION_KEYS: Record<
  keyof Margin,
  'margin_top' | 'margin_right' | 'margin_bottom' | 'margin_left'
> = {
  top: 'margin_top',
  right: 'margin_right',
  bottom: 'margin_bottom',
  left: 'margin_left',
};

const pageMarginToConverterOptions = (
  pageMargin?: Margin
): Partial<
  Record<
    'margin_top' | 'margin_right' | 'margin_bottom' | 'margin_left',
    string
  >
> => {
  if (!pageMargin) {
    return {};
  }

  const result: Partial<
    Record<
      'margin_top' | 'margin_right' | 'margin_bottom' | 'margin_left',
      string
    >
  > = {};

  (Object.keys(PDF_MARGIN_OPTION_KEYS) as (keyof Margin)[]).forEach((side) => {
    const value = pageMargin[side];
    if (value !== undefined && value !== '') {
      result[PDF_MARGIN_OPTION_KEYS[side]] = value;
    }
  });

  return result;
};

type GetDocumentBlobProps = {
  html: string;
  footerHtml?: string;
  pageMargin?: Margin;
  watermarkText?: string;
  isLandscape?: boolean;
  backgroundImage?: string;
  textPadding?: Padding;
  ignoreContentPadding?: boolean;
};

const fetchCss = async (path: string) => {
  const response = await fetch(path);
  return response.text();
};

const getStyles = async () => {
  const contentCss = await fetchCss('/styles/CKEditor/ck-content.css');
  const editorDefaultCss = await fetchCss('/styles/CKEditor/ckeditor5.css');
  const editorPremiumCss = await fetchCss(
    '/styles/CKEditor/ckeditor5-premium-features.css'
  );
  return `${contentCss}\n${editorDefaultCss}\n${editorPremiumCss}`;
};

export const getDocumentBlob = async ({
  html,
  footerHtml,
  pageMargin,
  watermarkText,
  isLandscape = false,
  backgroundImage,
  textPadding,
  ignoreContentPadding = false,
}: GetDocumentBlobProps): Promise<Blob> => {
  const token =
    process.env.NODE_ENV === 'development'
      ? (process.env.REACT_APP_CKEDITOR_TOKEN_URL as string)
      : ((await getCKEditorToken()) as string);

  const styles = await getStyles();

  const htmlWithHeader = addHeaderClassesToHtml(html);
  const htmlWithBackground = backgroundImage
    ? addBackgroundImage({ html: htmlWithHeader, backgroundImage, textPadding })
    : addContentPadding({ html: htmlWithHeader, textPadding });

  const resultHtml = addWatermark(
    ignoreContentPadding ? htmlWithHeader : htmlWithBackground,
    watermarkText || ''
  );

  const request = {
    html: resultHtml,
    css: styles,
    options: {
      format: 'A4',
      page_orientation: isLandscape ? 'landscape' : 'portrait',
      footer_html: footerHtml || '',
      ...pageMarginToConverterOptions(pageMargin),
    },
  };

  const res = await fetch('https://pdf-converter.cke-cs.com/v1/convert', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: token,
    },
    body: JSON.stringify(request),
  });

  const blob = await res.blob();
  return blob;
};
